import api from "../../services/api";
import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import { useReactToPrint } from "react-to-print";
import { useMenuAuthInfo } from "../../services/GlobalUtil";
import FunctionButton from "../../components/common/FunctionButton";
import ButtonWrapDiv from "../../components/common/ButtonWrapDiv";
import { useTranslation } from "react-i18next";
import ListWrapDiv from "../../components/common/ListWrapDiv";
import TopBoxDiv from "../../components/common/TopBoxDiv";
import TestDriveDiv from "../../components/common/TestDriveDiv";
import PressureDiv from "../../components/common/PressureDiv";
import VarianceDiv from "../../components/common/VarianceDiv";
import ActiveButton from "../../components/common/ActiveButton";
import ConditionDiv from "../../components/common/ConditionDiv";
import TimeDl from "../../components/common/TimeDl";
import MessageDialog from "../../components/common/MessageDialog";
import photoIcon from "../../assets/images/ico_photo.png";
import ImageDialog from "../../components/common/ImageDialog";
import FixImageDialog from "../../components/common/FixImageDialog";
import PdiDetailPrintPage from "./PdiDetailPrintPage";
import TableVisualInsp from "../../components/pidstatus/TableVisualInsp";
import LoadingBoxDiv from "../../components/common/LoadingBoxDiv";

const PdiDetailPage = ({ onClose, sId }) => {
  const menuAuthInfo = useMenuAuthInfo();

  const { t } = useTranslation();
  const [unitData, setUnitData] = useState("");
  const [infoData, setInfoData] = useState("");
  const [addInfo, setAddInfo] = useState("");
  const [autoCheckInfos, setAutoCheckInfos] = useState([]);
  const [visualCheckInfos, setVisualCheckInfos] = useState([]);
  const [testDriveInfos, setTestDriveInfos] = useState([]);
  const [wheelInfos, setWheelInfos] = useState([]);
  const [finalCheckInfos, setFinalCheckInfos] = useState([]);

  const [dialogMsg, setDialogMsg] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [dialogImage, setDialogImage] = useState("");
  const [dialogImageCmt, setDialogImageCmt] = useState("");
  const [isImageOpen, setIsImageOpen] = useState(false);

  const [isFixImageOpen, setIsFixImageOpen] = useState(false);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fixImageData, setFixImageData] = useState("");
  const [fixComment, setFixComment] = useState("");
  const [fixType, setFixType] = useState("");
  const [fixIdx, setFixIdx] = useState("");

  const printRef = useRef();

  useEffect(() => {
    if (!sId) {
      onClose();
    }

    if (menuAuthInfo.view) {
      setIsLoading(true);
      Promise.all([loadInfoData(), loadInspInfoData()]).finally(() => {
        setIsLoading(false);
      });
    }
  }, [sId]);

  const loadInfoData = () => {
    return api
      .post("/pdi-status/detail/info", {
        sId: sId,
      })
      .then((response) => {
        setInfoData(response.data.pdiInfo);
        setAddInfo(response.data.addInfo);
      })
      .catch(() => {});
  };

  const loadInspInfoData = () => {
    return api
      .post("/pdi-status/detail/insp", {
        sId: sId,
      })
      .then((response) => {
        setUnitData(response.data.unit);
        setAutoCheckInfos(response.data.autoCheckInfo);
        setVisualCheckInfos(response.data.visualCheckInfo);
        setTestDriveInfos(response.data.testDriveInfo);
        setWheelInfos(response.data.wheelInfo);
        setFinalCheckInfos(response.data.finalCheckInfo);
      })
      .catch(() => {});
  };

  const handleClickClose = () => {
    setInfoData("");
    setAddInfo("");
    onClose();
  };

  const handleExcelClick = () => {
    if (isExcelLoading) {
      return;
    }
    setIsExcelLoading(true);
    api
      .post(
        "/pdi-status/detail/excel",
        {
          sId: sId,
        },
        {
          responseType: "blob",
        }
      )
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        link.setAttribute(
          "download",
          "AreaInspStatus_" + moment().format("YYYYMMDD") + ".xlsx"
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        setIsExcelLoading(false);
      })
      .catch(() => {
        setIsExcelLoading(false);
      });
  };

  const handlePrintClick = useReactToPrint({
    contentRef: printRef,
    pageStyle: `
    @page {
      size: A4 portrait;
      margin: 0 0 0 10mm; 
    }
    @media print {
          * {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      body {
        width: 21cm;
        height: 29.7cm;
      }
    }
  `,
  });

  const handleRawDataClick = () => {
    api
      .post(
        "/pdi-status/detail/rawdata",
        {
          sId: sId,
        },
        {
          responseType: "blob",
        }
      )
      .then((response) => {
        const disposition = response.headers["content-disposition"];

        // .REP 파일이 1개면 해당 파일, 2개 이상이면 zip 파일로 내려옴
        const matches = disposition && disposition.match(/filename="(.+)"/);
        const fileName =
          matches && matches[1] ? matches[1] : "RawData_" + sId + ".zip";

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;

        link.setAttribute("download", fileName);

        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(async (error) => {
        // responseType 이 blob 이므로 에러 응답도 Blob 으로 내려옴
        let result;
        try {
          const data = error.response?.data;
          result =
            data instanceof Blob ? JSON.parse(await data.text()).result : data?.result;
        } catch {
          result = undefined;
        }
        if (result == "1071") {
          setDialogMsg(t("L0715"));
          setIsDialogOpen(true);
        }
      });
  };

  const handleImageClick = (fileName, comment) => {
    api
      .post(
        "/pdi-status/detail/image",
        {
          sId: sId,
          sFileName: fileName,
        },
        {
          responseType: "blob",
        }
      )
      .then((response) => {
        setDialogImage(new Blob([response.data], { type: "image/*" }));
        setDialogImageCmt(comment);
        setIsImageOpen(true);
      })
      .catch(() => {
        setDialogMsg(t("L1089"));
        setIsDialogOpen(true);
      });
  };

  const handleDialogConfirm = () => {
    setDialogMsg("");
    setIsDialogOpen(false);
  };

  const handleImageDialogConfirm = () => {
    setDialogImage("");
    setDialogImageCmt("");
    setIsImageOpen(false);
  };

  const handleFixImageClick = (type, idx) => {
    setIsFixImageOpen(true);
    setFixType(type);
    setFixIdx(idx);
  };

  const handleFixImageDialogReg = () => {
    const formData = new FormData();

    const imageData = {
      sId: sId,
      sType: fixType,
      sIdx: fixIdx,
      comment: fixComment,
    };

    formData.append(
      "imageDataVO",
      new Blob([JSON.stringify(imageData)], { type: "application/json" })
    );

    if (fixImageData) {
      formData.append("file", fixImageData);
    }

    api
      .post("/pdi-status/detail/fix-image", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      .then(() => {
        setIsFixImageOpen(false);
        setFixImageData("");
        setFixComment("");
        loadInfoData();
        loadInspInfoData();
      })
      .catch(() => {});
  };

  const handleFixImageDialogCancel = () => {
    setIsFixImageOpen(false);
    setFixImageData("");
    setFixComment("");
  };

  return (
    <div id="wrap">
      <LoadingBoxDiv loading={isLoading} contained>
      <div id="contents">
        <br />
        <ButtonWrapDiv className="btn_top">
          <FunctionButton
            className="bg_gray"
            style={{ fontSize: "13px" }}
            value={t("L0750")}
            onClick={handleClickClose}
          ></FunctionButton>
          <FunctionButton
            style={{ fontSize: "13px" }}
            value={t("L0150")}
            onClick={handleExcelClick}
            loading={isExcelLoading}
          ></FunctionButton>
          <FunctionButton
            style={{ fontSize: "13px" }}
            value={t("L0184")}
            onClick={handlePrintClick}
          ></FunctionButton>
          <FunctionButton
            style={{ fontSize: "13px" }}
            value={t("L0180")}
            onClick={handleRawDataClick}
          ></FunctionButton>
        </ButtonWrapDiv>
        <br />

        <TopBoxDiv value={t("L0330")}>
          <ListWrapDiv className={"list_hori mt10"}>
            <table>
              <colgroup>
                <col style={{ width: "20%" }} />
                <col style={{ width: "30%" }} />
                <col style={{ width: "20%" }} />
                <col style={{ width: "30%" }} />
              </colgroup>
              <tbody>
                <tr>
                  <th>{t("L0145")}</th>
                  <td>{infoData.vehicle}</td>
                  <th>{t("L0151")}</th>
                  <td>
                    {infoData.inspDate
                      ? moment(infoData.inspDate).format("YYYY-MM-DD HH:mm:ss")
                      : ""}
                  </td>
                </tr>
                <tr>
                  <th>{t("L0158")}</th>
                  <td>{infoData.year}</td>
                  <th>{t("L0163")}</th>
                  <td>{infoData.engine}</td>
                </tr>
                <tr>
                  <th>{t("L0156")}</th>
                  <td>{infoData.inspector}</td>
                  <th>{t("L0051")}</th>
                  <td>{infoData.loc}</td>
                </tr>
                <tr>
                  <th>{t("L0671")}</th>
                  <td>{infoData.vin}</td>
                  <th>{t("L0228")}</th>
                  <td>
                    {infoData.inspTime
                      ? Math.floor(infoData.inspTime / 60) +
                        "’ " +
                        (infoData.inspTime % 60).toString().padStart(2, "0") +
                        "”"
                      : ""}
                  </td>
                </tr>
                <tr>
                  <th>{t("L1080")}</th>
                  <td></td>
                  <th>{t("L0703")}</th>
                  <td>{infoData.mileage}</td>
                </tr>
                <tr>
                  <th>{t("L0153")}</th>
                  <td>{infoData.totalItemCount}</td>
                  <th>{t("L0154")}</th>
                  <td>{infoData.defectCount}</td>
                </tr>
              </tbody>
            </table>
          </ListWrapDiv>
        </TopBoxDiv>

        <TopBoxDiv value={t("L0402")}>
          <ListWrapDiv className={"list_hori mt10"}>
            <table>
              <colgroup>
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
                <col style={{ width: "12%" }} />
              </colgroup>
              <tbody>
                <tr>
                  <th>{t("L0403")}</th>
                  <td>{addInfo.batteryVolt}</td>
                  <th>{t("L0680")}</th>
                  <td>{addInfo.batterySoc}</td>
                  <th>{t("L0404")}</th>
                  <td>{addInfo.cca}</td>
                  <th>{t("L0152")}</th>
                  <td>
                    <a href="#" onClick={() => handleImageClick("")}>
                      {addInfo.resultFile ? (
                        <img src={photoIcon} alt="photo" />
                      ) : (
                        ""
                      )}
                    </a>
                  </td>
                </tr>
                <tr>
                  <th>{t("L0405")}</th>
                  <td colSpan={7}>{addInfo.roNo}</td>
                </tr>
              </tbody>
            </table>
          </ListWrapDiv>
        </TopBoxDiv>

        {autoCheckInfos?.length > 0 && (
          <TopBoxDiv value={t("L0734")}>
            <ListWrapDiv className={"mt10"}>
              <table style={{ tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "auto" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "13%" }} />
                  <col style={{ width: "9%" }} />
                </colgroup>
                <tbody>
                  <tr>
                    <th>{t("L0130")}</th>
                    <th>{t("L0086")}</th>
                    <th>{t("L0176")}</th>
                    <th style={{ textAlign: "left" }}>{t("L0127")}</th>
                    <th>{t("L1021")}</th>
                    <th>{t("L0251")}</th>
                    <th>{t("L0177")}</th>
                    <th>{t("L0252")}</th>
                    <th>{t("L0254")}</th>
                    <th>{t("L0255")}</th>
                  </tr>
                  {autoCheckInfos?.length > 0 ? (
                    autoCheckInfos
                      .filter(
                        (autoCheckInfo) =>
                          !String(autoCheckInfo.itemCode).startsWith("90")
                      )
                      .map((autoCheckInfo, rowIndex) => (
                        <tr key={rowIndex}>
                          <td>{autoCheckInfo.idx}</td>
                          <td>{autoCheckInfo.itemCode}</td>
                          <td>
                            <ActiveButton
                              onClick={
                                autoCheckInfo.result == 1 && menuAuthInfo.edit
                                  ? () =>
                                      handleFixImageClick(
                                        autoCheckInfo.type,
                                        autoCheckInfo.idx
                                      )
                                  : undefined
                              }
                              {...(autoCheckInfo.result != 2 && {
                                isActive: !autoCheckInfo.result,
                              })}
                              style={{
                                cursor:
                                  autoCheckInfo.result == 1
                                    ? "pointer"
                                    : "default",
                              }}
                            ></ActiveButton>
                          </td>
                          <td style={{ textAlign: "left" }}>
                            {autoCheckInfo.inspItem}
                          </td>
                          <td>{autoCheckInfo.unit}</td>
                          <td>{autoCheckInfo.min}</td>
                          <td>{autoCheckInfo.actualValue}</td>
                          <td>{autoCheckInfo.max}</td>
                          <td
                            style={{
                              wordWrap: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            {autoCheckInfo.comment}
                          </td>
                          <td>
                            <a
                              href="#"
                              onClick={() =>
                                handleImageClick(
                                  autoCheckInfo.fileName,
                                  autoCheckInfo.comment
                                )
                              }
                            >
                              {autoCheckInfo.fileName ? (
                                <img src={photoIcon} alt="photo" />
                              ) : (
                                ""
                              )}
                            </a>
                          </td>
                        </tr>
                      ))
                  ) : (
                    <tr></tr>
                  )}
                </tbody>
              </table>
            </ListWrapDiv>
          </TopBoxDiv>
        )}

        {visualCheckInfos?.length > 0 && (
          <TopBoxDiv value={t("L0217")}>
            <ListWrapDiv className={"mt10"}>
              <TableVisualInsp list={visualCheckInfos} />
            </ListWrapDiv>
          </TopBoxDiv>
        )}

        {testDriveInfos?.length > 0 && (
          <TopBoxDiv value={t("L0168")}>
            <TestDriveDiv className={"mt10"}>
              <table>
                <colgroup>
                  <col style={{ width: "24%" }} />
                  <col style={{ width: "54%" }} />
                  <col style={{ width: "22%" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>{t("L0393")}</th>
                    <th>{t("L0181")}</th>
                    <th>{t("L0748")}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="items">
                    <td>
                      <ConditionDiv>
                        <dl className="ng">
                          <dt>{t("L0743")}</dt>
                          <dd>0</dd>
                        </dl>
                        <dl className="ng">
                          <dt>{t("L0744")}</dt>
                          <dd>0</dd>
                        </dl>
                        <dl className="ng">
                          <dt>{t("L0738")}</dt>
                          <dd>0</dd>
                        </dl>
                      </ConditionDiv>
                    </td>
                    <td>
                      <PressureDiv>
                        <dl className={"p_fl na"}>
                          <dt>{t("L0739")}</dt>
                          <dd>0</dd>
                        </dl>
                        <dl className={"p_fr ok"}>
                          <dt>{t("L0740")}</dt>
                          <dd>0</dd>
                        </dl>
                        <dl className={"p_rl ok "}>
                          <dt>{t("L0741")}</dt>
                          <dd>0</dd>
                        </dl>
                        <dl className={"p_rr ng"}>
                          <dt>{t("L0742")}</dt>
                          <dd>0</dd>
                        </dl>
                      </PressureDiv>
                    </td>
                    <td>
                      <VarianceDiv>
                        <dl className={"v_flfr ok"}>
                          <dt>{t("L0746")}</dt>
                          <dd>0</dd>
                        </dl>
                        <dl className={"v_rlrr ok"}>
                          <dt>{t("L0747")}</dt>
                          <dd>0</dd>
                        </dl>
                      </VarianceDiv>
                    </td>
                  </tr>
                  <tr>
                    <td colSpan={3}>
                      <TimeDl>
                        <dt>{t("L0934")}</dt>
                        <dd>test</dd>
                        <dt>{t("L0935")}</dt>
                        <dd>test</dd>
                      </TimeDl>
                    </td>
                  </tr>
                </tbody>
              </table>
            </TestDriveDiv>

            <ListWrapDiv className={"mt10"}>
              <table style={{ tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "auto" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "10%" }} />
                </colgroup>
                <tbody>
                  <tr>
                    <th>{t("L0130")}</th>
                    <th>{t("L0086")}</th>
                    <th>{t("L0176")}</th>
                    <th style={{ textAlign: "left" }}>{t("L0127")}</th>
                    <th>{t("L0254")}</th>
                    <th>{t("L0255")}</th>
                  </tr>
                  {autoCheckInfos?.length > 0 &&
                    autoCheckInfos
                      .filter((autoCheckInfo) =>
                        String(autoCheckInfo.itemCode).startsWith("90")
                      )
                      .map((autoCheckInfo, rowIndex) => (
                        <tr key={rowIndex}>
                          <td>{autoCheckInfo.idx}</td>
                          <td>{autoCheckInfo.itemCode}</td>
                          <td>
                            <ActiveButton
                              onClick={
                                autoCheckInfo.result == 1 && menuAuthInfo.edit
                                  ? () =>
                                      handleFixImageClick(
                                        autoCheckInfo.type,
                                        autoCheckInfo.idx
                                      )
                                  : undefined
                              }
                              {...(autoCheckInfo.result != 2 && {
                                isActive: !autoCheckInfo.result,
                              })}
                              style={{
                                cursor:
                                  autoCheckInfo.result == 1
                                    ? "pointer"
                                    : "default",
                              }}
                            ></ActiveButton>
                          </td>
                          <td>{autoCheckInfo.inspItem}</td>
                          <td
                            style={{
                              wordWrap: "break-word",
                              whiteSpace: "normal",
                            }}
                          >
                            {autoCheckInfo.comment}
                          </td>
                          <td>
                            <a
                              href="#"
                              onClick={() =>
                                handleImageClick(
                                  autoCheckInfo.fileName,
                                  autoCheckInfo.comment
                                )
                              }
                            >
                              {autoCheckInfo.fileName ? (
                                <img src={photoIcon} alt="photo" />
                              ) : (
                                ""
                              )}
                            </a>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </ListWrapDiv>
          </TopBoxDiv>
        )}

        {finalCheckInfos?.length > 0 && (
          <TopBoxDiv value={t("L0396")}>
            <ListWrapDiv className={"mt10"}>
              <table style={{ tableLayout: "fixed" }}>
                <colgroup>
                  <col style={{ width: "6%" }} />
                  <col style={{ width: "8%" }} />
                  <col style={{ width: "10%" }} />
                  <col style={{ width: "auto" }} />
                  <col style={{ width: "12%" }} />
                  <col style={{ width: "10%" }} />
                </colgroup>
                <tbody>
                  <tr>
                    <th>{t("L0130")}</th>
                    <th>{t("L0086")}</th>
                    <th>{t("L0176")}</th>
                    <th style={{ textAlign: "left" }}>{t("L0127")}</th>
                    <th>{t("L0254")}</th>
                    <th>{t("L0255")}</th>
                  </tr>
                  {finalCheckInfos?.length > 0 &&
                    finalCheckInfos.map((finalCheckInfo, rowIndex) => (
                      <tr key={rowIndex}>
                        <td>{finalCheckInfo.idx}</td>
                        <td>{finalCheckInfo.itemCode}</td>
                        <td>
                          <ActiveButton
                            onClick={
                              finalCheckInfo.result == 1 && menuAuthInfo.edit
                                ? () =>
                                    handleFixImageClick(
                                      finalCheckInfo.type,
                                      finalCheckInfo.idx
                                    )
                                : undefined
                            }
                            {...(finalCheckInfo.result != 2 && {
                              isActive: !finalCheckInfo.result,
                            })}
                            style={{
                              cursor:
                                finalCheckInfo.result == 1
                                  ? "pointer"
                                  : "default",
                            }}
                          ></ActiveButton>
                        </td>
                        <td>{finalCheckInfo.inspItem}</td>
                        <td
                          style={{
                            wordWrap: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          {finalCheckInfo.comment}
                        </td>
                        <td>
                          <a
                            href="#"
                            onClick={() =>
                              handleImageClick(
                                finalCheckInfo.fileName,
                                finalCheckInfo.comment
                              )
                            }
                          >
                            {finalCheckInfo.fileName ? (
                              <img src={photoIcon} alt="photo" />
                            ) : (
                              ""
                            )}
                          </a>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </ListWrapDiv>
          </TopBoxDiv>
        )}

        <br />
        <ButtonWrapDiv className="btn_top">
          <FunctionButton
            className="bg_gray"
            style={{ fontSize: "13px" }}
            value={t("L0750")}
            onClick={handleClickClose}
          ></FunctionButton>
          <FunctionButton
            style={{ fontSize: "13px" }}
            value={t("L0150")}
            onClick={handleExcelClick}
            loading={isExcelLoading}
          ></FunctionButton>
          <FunctionButton
            style={{ fontSize: "13px" }}
            value={t("L0184")}
            onClick={handlePrintClick}
          ></FunctionButton>
          <FunctionButton
            style={{ fontSize: "13px" }}
            value={t("L0180")}
            onClick={handleRawDataClick}
          ></FunctionButton>
        </ButtonWrapDiv>
        <br />

        {isDialogOpen && (
          <MessageDialog title={t("L0339")} message={dialogMsg}>
            <FunctionButton
              value={t("L0004")}
              onClick={handleDialogConfirm}
            ></FunctionButton>
          </MessageDialog>
        )}

        {isImageOpen && (
          <ImageDialog
            title={t("L0255")}
            imageData={dialogImage}
            comment={dialogImageCmt}
          >
            <FunctionButton
              className="bg_gray big"
              value={t("L0750")}
              onClick={handleImageDialogConfirm}
            ></FunctionButton>
          </ImageDialog>
        )}

        {isFixImageOpen && (
          <FixImageDialog
            title={t("L0756")}
            imageData={fixImageData}
            comment={fixComment}
            emitImageData={setFixImageData}
            emitComment={setFixComment}
          >
            <FunctionButton
              className="big"
              value={t("L0016")}
              onClick={handleFixImageDialogReg}
            ></FunctionButton>
            <FunctionButton
              className="bg_gray big"
              value={t("L0005")}
              onClick={handleFixImageDialogCancel}
            ></FunctionButton>
          </FixImageDialog>
        )}

        <div style={{ display: "none" }}>
          <PdiDetailPrintPage
            unit={unitData}
            info={infoData}
            add={addInfo}
            auto={autoCheckInfos}
            visual={visualCheckInfos}
            testDrive={testDriveInfos}
            wheel={wheelInfos}
            final={finalCheckInfos}
            ref={printRef}
          />
        </div>
      </div>
      </LoadingBoxDiv>
    </div>
  );
};

export default PdiDetailPage;
